/**
 * recentActivityStore — per-project "Claude has done something in the last
 * 5 minutes AND isn't sitting idle at the prompt" tracker. Drives the
 * footer activity light + the popover that lists currently-active projects.
 *
 * Distinct from `projectActivityStore`, which is the 3-second sidebar pulse —
 * different window, different rule for when activity "ends":
 *
 *   ┌──────────────────────────────────────────────┬─────────────────────────┐
 *   │ store                  │ window │ ends when… │ ui                       │
 *   ├────────────────────────┼────────┼────────────┼──────────────────────────┤
 *   │ projectActivityStore   │ 3s     │ timer      │ sidebar pulse            │
 *   │ recentActivityStore    │ 5m     │ pattern    │ footer light + popover   │
 *   └──────────────────────────────────────────────┴──────────────────────────┘
 *
 * The pattern-match signal (Claude's prompt is back) immediately suppresses
 * the green light even if the 5-minute window hasn't expired — that's the
 * "unless the last thing Claude said is that he's gone" rule from the user.
 *
 * The store re-renders subscribers via a 30s tick so the sliding window
 * naturally expires even when no events are coming in.
 */
import { create } from 'zustand';

/** How long after the last activity event a project is still considered "active". */
export const ACTIVITY_WINDOW_MS = 5 * 60 * 1000; // 5 minutes

interface ProjectActivity {
  lastActivityAt: number;
  lastCompletionAt: number;
}

interface RecentActivityState {
  activity: Map<string, ProjectActivity>;
  /** Bumped every 30s so subscribers re-render and the 5-minute window
   *  slides closed correctly even when no events come in. */
  tickCounter: number;

  recordActivity: (projectPath: string) => void;
  recordCompletion: (projectPath: string) => void;
  /** Pure helper — call from selectors / render. Pass `now` for tests. */
  isActive: (projectPath: string, now?: number) => boolean;
  bumpTick: () => void;
}

export const useRecentActivityStore = create<RecentActivityState>((set, get) => ({
  activity: new Map(),
  tickCounter: 0,

  recordActivity: (projectPath) =>
    set((state) => {
      const next = new Map(state.activity);
      const existing = next.get(projectPath);
      next.set(projectPath, {
        lastActivityAt: Date.now(),
        lastCompletionAt: existing?.lastCompletionAt ?? 0,
      });
      return { activity: next };
    }),

  recordCompletion: (projectPath) =>
    set((state) => {
      const next = new Map(state.activity);
      const existing = next.get(projectPath);
      next.set(projectPath, {
        lastActivityAt: existing?.lastActivityAt ?? 0,
        lastCompletionAt: Date.now(),
      });
      return { activity: next };
    }),

  isActive: (projectPath, now = Date.now()) => {
    const a = get().activity.get(projectPath);
    if (!a) return false;
    if (now - a.lastActivityAt > ACTIVITY_WINDOW_MS) return false;
    return a.lastActivityAt > a.lastCompletionAt;
  },

  bumpTick: () => set((s) => ({ tickCounter: s.tickCounter + 1 })),
}));

let tickInterval: ReturnType<typeof setInterval> | null = null;

/** Start the 30s tick that re-evaluates the sliding 5-minute window.
 *  Idempotent — safe to call from app startup or per-mount. */
export function startRecentActivityTicker(): void {
  if (tickInterval) return;
  tickInterval = setInterval(() => {
    useRecentActivityStore.getState().bumpTick();
  }, 30_000);
}

export function stopRecentActivityTicker(): void {
  if (tickInterval) {
    clearInterval(tickInterval);
    tickInterval = null;
  }
}
